import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tag } from '../components/Tag';
import { Card } from '../components/Card';
import { HR } from '../components/HR';
import { Button } from '../components/Button';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import styles from './YouScreen.module.css';

interface UserProfile {
  id: number;
  email: string;
  display_name: string;
  role: string;
  vouch_count: number;
  status: string;
  vouched_by_name?: string | null;
}

interface PlatformAnalytics {
  events: { total: string };
  tickets: { total: string; checked_in: string };
  members: { total: string; vouched: string; admins: string; volunteers: string };
  recent_activity: { type: string; count: string; last_activity: string }[];
}

export function YouScreen() {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<PlatformAnalytics | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [following, setFollowing] = useState<{ id: number; follower_id: number; following_id: number; following_type: string; created_at: string; display_name: string | null }[]>([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMessages, setChatMessages] = useState<{ role: 'user' | 'hermes'; text: string; source?: string }[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [faqItems, setFaqItems] = useState<Array<{ category: string; response: string; keywords: string[] }>>([]);
  const [faqLoading, setFaqLoading] = useState(false);
  const chatEndRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (chatEndRef.current) chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  useEffect(() => {
    if (!chatOpen) return;
    setFaqLoading(true);
    api.concierge.faq()
      .then(res => setFaqItems(res.faqs))
      .catch(() => setFaqItems([]))
      .finally(() => setFaqLoading(false));
  }, [chatOpen]);

  const faqQuestions: Record<string, string> = {
    'check-in': 'What time is check-in?',
    'check-out': 'What time is check-out?',
    'amenities': 'What amenities do you have?',
    'policies': 'What are your policies?',
    'events': 'What events are happening tonight?',
    'location': 'How do I get to your location?',
  };

  const handleFaqChip = (category: string) => {
    const question = faqQuestions[category] || `Tell me about ${category}`;
    sendChat(question);
  };

  const sendChat = async (overrideText?: string) => {
    const text = (overrideText ?? chatInput).trim();
    if (!text || chatSending) return;
    setChatMessages(prev => [...prev, { role: 'user', text }]);
    setChatInput('');
    setChatSending(true);
    setChatError(null);
    try {
      // Prepare history for the LLM - keep only the last 8 turns
      const history = chatMessages.slice(-8).map(msg => ({
        role: msg.role === 'hermes' ? 'assistant' as const : 'user' as const,
        content: msg.text
      }));
      
      const res = await api.concierge.send(text, history);
      setChatMessages(prev => [...prev, { role: 'hermes', text: res.response, source: res.source }]);
    } catch (error) {
      console.error('Concierge error:', error);
      setChatError('The assistant is unavailable right now. Please try again.');
    } finally {
      setChatSending(false);
    }
  };

  const sourceLabel = (source?: string) =>
    source === 'home_assistant' ? 'Home Assistant' : source === 'llm' ? 'LLM' : source === 'rule_based' ? 'FAQ' : 'OnIsland';

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const data = await api.auth.profile();
        setProfile(data);
      } catch (error) {
        console.error('Failed to fetch profile:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!user || user.role !== 'admin') return;
      try {
        setAnalyticsLoading(true);
        const data = await api.analytics.platform();
        setAnalytics(data);
      } catch (error) {
        console.error('Failed to fetch analytics:', error);
      } finally {
        setAnalyticsLoading(false);
      }
    };
    fetchAnalytics();
  }, [user?.role]);

  useEffect(() => {
    const fetchFollowing = async () => {
      try {
        const data = await api.following.list();
        setFollowing(data);
      } catch (error) {
        console.error('Failed to fetch following:', error);
      }
    };
    fetchFollowing();
  }, []);

  if (loading || !profile || !user) {
    return (
      <div className={styles.loadingContainer}>
        <h2 className={styles.loadingHeader}>You</h2>
        <p>Loading...</p>
      </div>
    );
  }

  const vouchText = profile.vouch_count === 1 ? '1 co-sign' : `${profile.vouch_count} co-signs`;
  const vouchedBy = profile.status === 'vouched' ? 'vouched in' : 'pending vouch';

  return (
    <div>
      <div className={styles.container}>
        <h2 className={styles.header}>You</h2>

        <Card kicker="Trust standing" title={`Good — ${vouchedBy} by ${profile.vouched_by_name || 'Not yet vouched'}`} body={`${vouchText} · 0 flags`} elevation="sm" />

        <HR />

        {user.role === 'admin' && (
          <Card
            kicker="Platform overview"
            title="Stats"
            body={
              analyticsLoading
                ? 'Loading...'
                : analytics
                ? `${analytics.events.total} events · ${analytics.tickets.total} tickets sold · ${analytics.tickets.checked_in} checked in · ${analytics.members.vouched} vouched members`
                : 'Unable to load analytics'
            }
            elevation="sm"
          />
        )}

        <div className={styles.sectionLabel}>Following</div>
        <div className={styles.tagRow}>
          {following.length > 0 ? (
            following.map(f => <Tag key={f.id} variant="accent" onClick={() => {
              api.following.unfollow(f.id).then(() => {
                setFollowing(prev => prev.filter(item => item.id !== f.id));
              }).catch(err => console.error('Failed to unfollow:', err));
            }}>{f.display_name ?? 'Unknown'} ✕</Tag>)
          ) : (
            <span className={styles.emptyState}>Not following anyone yet</span>
          )}
        </div>

        <div className={styles.sectionLabel}>Reachable on</div>
        <div className={styles.tagRow}>
          <Tag variant="neutral">Email</Tag>
          <Tag variant="neutral">Signal</Tag>
        </div>

        <Button
          className="btn-block"
          style={{ justifyContent: 'center', marginBottom: '8px' }}
          onClick={() => setChatOpen(open => !open)}
        >
          Ask Hermes
        </Button>

        {chatOpen && (
          <div className={styles.chatContainer}>
          <Card kicker="OnIsland assistant" title="Hermes" elevation="sm">
            <div className={styles.chatMessages}>
              {chatMessages.length === 0 && (
                <>
                  <p className={styles.chatPrompt}>
                    Ask me about events, tickets, venue info, or island life.
                  </p>
                  {!faqLoading && faqItems.length > 0 && (
                    <div className={styles.faqChips}>
                      {faqItems.map(faq => {
                        const label = faq.category.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                        return (
                          <button
                            key={faq.category}
                            className={styles.faqChip}
                            onClick={() => handleFaqChip(faq.category)}
                          >
                            {label}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
              {chatMessages.map((m, i) => (
                <div key={i} className={styles.chatMessageRow} style={{ justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                  <div
                    className={`${styles.chatBubble} ${m.role === 'user' ? styles.chatBubbleUser : styles.chatBubbleHermes}`}
                  >
                    {m.text}
                    {m.role === 'hermes' && m.source && (
                      <div className={styles.chatSource}>
                        {sourceLabel(m.source)}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {chatSending && (
                <div className={styles.chatThinking}>Hermes is thinking…</div>
              )}
              <div ref={chatEndRef} />
            </div>
            {chatError && (
              <div className={styles.chatError}>
                {chatError}
              </div>
            )}
            <div className={styles.chatInputRow}>
              <input
                type="text"
                value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') sendChat(); }}
                placeholder="Ask something…"
                className={styles.chatInput}
              />
              <Button
                style={{ justifyContent: 'center' }}
                disabled={chatSending || !chatInput.trim()}
                onClick={() => sendChat()}
              >
                Send
              </Button>
            </div>
          </Card>
          </div>
        )}

        <Button className={`btn-block ${styles.settingsButton}`} style={{ justifyContent: 'center' }} onClick={() => navigate('/settings')}>
          Settings
        </Button>

        <Button className={`btn-block ${styles.logoutButton}`} style={{ justifyContent: 'center' }} onClick={() => { logout(); navigate('/login'); }}>
          Logout
        </Button>
      </div>
    </div>
  );
}
